import { createBerthCallChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/BerthCallChangeDataFactory';
import { createCargoOperationChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoOperationChangeDataFactory';
import { createPortCallChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/RotationPortCallChangeDataFactory';
import { createDeleteStowagePlanChange, createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import {
    compareBerthCalls,
    compareCargoes,
    compareCargoOperations,
    comparePortCalls,
    setIgnoredMergePropertiesInChangeData,
} from './comparisonUtils';
import { getChangeDataGuid, setChangeDataGuid } from '../stowagePlanChangeUtils/stowageChangeDataTypeUtils';
import {
    findByPortCallId,
    findByBerthCallId,
    findByCargoOperationId,
    RotationItem,
    getItems,
    getItemImosId,
    findByCargoId,
    RotationElement,
    findByPortCallMergeProperties,
    findByBerthCallMergeProperties,
    hasMatchingSpiltCargoOperations,
} from './itemFindersUtils';
import { createCargoChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoChangeDataFactory';
import { isManualCall, isVoyageClosedOrCompleted } from '../stowageRotationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { UUID } from 'crypto';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';

interface ComparisonData {
    stowagePlanGuid: UUID;
    stowagePlanTransitOperations: RotationPortCall[];
    existingRotation: RotationPortCall[];
    incomingRotation: RotationPortCall[];
    existingCargoes: RotationCargo[];
    incomingCargoes: RotationCargo[];
}
export const getNumberOfClosedSyncChanges = (changes: StowagePlanChange[]): number =>
    getNumberOfChanges(filterSyncChangesByOpenOrClosed(changes, true));
export const getNumberOfOpenSyncChanges = (changes: StowagePlanChange[]): number =>
    getNumberOfChanges(filterSyncChangesByOpenOrClosed(changes, false));

export const getNumberOfChanges = (changes: StowagePlanChange[]): number =>
    changes.reduce((acc, change) => {
        const extraData = JSON.parse(change.data ?? '{}')?.extraData;
        if (extraData?.hideFromChangeLog) return acc;
        const descriptions = extraData?.descriptions;
        return acc + (descriptions?.length ?? 1);
    }, 0);
export const filterSyncChangesByOpenOrClosed = (changes: StowagePlanChange[], shouldBeClosedOrCompleted: boolean) => {
    if (!changes) return [];
    return changes.filter((change) => {
        if (!change.data) return false;
        const dataJson = change.data;
        const data = dataJson ? JSON.parse(dataJson) : null;

        return shouldBeClosedOrCompleted ? !data.extraData?.isChangeInOpenVoyage : data.extraData?.isChangeInOpenVoyage;
    });
};

export const compareRotations = (comparisonData: ComparisonData): StowagePlanChange[] => {
    // For cargoes and cargo operations, we pass a finder that always returns null to disable auto-merging.
    // Manual cargoes/cargo operations should remain separate from IMOS cargoes/cargo operations,
    // allowing users to manually merge them via the context menu when appropriate.
    const noAutoMergeForCargoes = () => null;

    const changes = [
        ...checkItems(
            comparisonData,
            findByPortCallId,
            findByPortCallMergeProperties,
            StowageChangeDataType.RotationPortCall,
            createPortCallChangeData,
            comparePortCalls
        ),
        ...checkItems(
            comparisonData,
            findByBerthCallId,
            findByBerthCallMergeProperties,
            StowageChangeDataType.RotationBerthCall,
            createBerthCallChangeData,
            compareBerthCalls
        ),
        ...checkItems(
            comparisonData,
            findByCargoId,
            noAutoMergeForCargoes,
            StowageChangeDataType.RotationCargo,
            createCargoChangeData,
            compareCargoes
        ),
        ...checkItems(
            comparisonData,
            findByCargoOperationId,
            noAutoMergeForCargoes,
            StowageChangeDataType.RotationCargoOperation,
            createCargoOperationChangeData,
            compareCargoOperations
        ),
    ];

    // Re-parent orphaned manual cargo operations whose parent berth call was deleted.
    // When IMOS replaces a berth call (delete old + add new), manual cargo operations under
    // the deleted berth are protected from deletion by !isManualCall(), but their berthCallGuid
    // still points to the now-deleted berth. Without re-parenting, they become orphaned during
    // plan rebuild (stowageDataItemsBuilder silently drops them). See ADO #35015.
    changes.push(...getReParentedManualCargoOperations(comparisonData, changes));

    return changes;
};

type ChangeOperationType = 'Added' | 'Deleted' | 'Merged' | undefined;

const getDescriptor = (changeDataType: StowageChangeDataType, changeElement?: RotationElement, operationType?: ChangeOperationType) => {
    const operationPrefix = operationType ? `${operationType} ` : '';

    if (changeDataType === StowageChangeDataType.RotationPortCall) return `${operationPrefix}Port Call`;
    if (changeDataType === StowageChangeDataType.RotationBerthCall) return `${operationPrefix}Berth Call`;
    if (changeDataType === StowageChangeDataType.RotationCargoOperation) {
        const cargoOperation = changeElement as RotationCargoOperation;
        return `${operationPrefix}Cargo Operation ${cargoOperation.operationType ?? ''} ${cargoOperation.contractWeight}MT`;
    }
    if (changeDataType === StowageChangeDataType.RotationCargo) return `${operationPrefix}Cargo`;

    return '';
};

const checkItems = (
    comparisonData: ComparisonData,
    findItemById: (id: number, list: RotationPortCall[] | RotationCargo[]) => RotationElement,
    findItemByMergeProperties: (
        rotationElement: RotationElement,
        existingList: RotationPortCall[] | RotationCargo[],
        extraArgs?: any
    ) => RotationElement,
    changeDataType: StowageChangeDataType,
    createChangeData?: (item: RotationElement) => Partial<RotationElement>,
    compareItems?: (stowagePlanGuid: UUID, existingItem: RotationItem, incomingItem: RotationItem) => StowagePlanChange | null
): StowagePlanChange[] => {
    const existingItems = getItems(
        changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
        changeDataType
    );
    const incomingItems = getItems(
        changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.incomingCargoes : comparisonData.incomingRotation,
        changeDataType
    );

    // Build O(1) lookup indexes for existing and incoming items by IMOS ID/GUID
    const existingByImosId = new Map<number | UUID, RotationElement>();
    for (const item of existingItems) {
        const id = getItemImosId(changeDataType, item.rotationElement);
        if (id != null) existingByImosId.set(id, item.rotationElement);
    }
    const incomingByImosId = new Map<number | UUID, RotationElement>();
    const incomingItemByImosId = new Map<number | UUID, RotationItem>();
    for (const item of incomingItems) {
        const id = getItemImosId(changeDataType, item.rotationElement);
        if (id != null) {
            incomingByImosId.set(id, item.rotationElement);
            incomingItemByImosId.set(id, item);
        }
    }

    setSyncStatus(comparisonData, changeDataType, existingItems, incomingItems, incomingItemByImosId);
    assignCorrectGuids(changeDataType, incomingItems, comparisonData);
    const mergedItems = getMergedItems(
        incomingItems,
        findItemByMergeProperties,
        comparisonData,
        changeDataType,
        comparisonData.stowagePlanTransitOperations,
        createChangeData
    );

    return [
        ...mergedItems,
        ...getDeletedItems(existingItems, incomingByImosId, comparisonData, changeDataType, mergedItems),
        ...getAddedItems(
            incomingItems,
            existingByImosId,
            comparisonData,
            changeDataType,
            comparisonData.stowagePlanTransitOperations,
            createChangeData,
            mergedItems
        ),
        ...getUpdatedItems(existingItems, incomingByImosId, incomingItemByImosId, comparisonData, changeDataType, compareItems),
    ];
};

const assignCorrectGuids = (changeDataType: StowageChangeDataType, incomingItems: RotationItem[], comparisonData: ComparisonData): void => {
    // Build lookup maps once for O(1) lookups instead of per-item .find()
    const existingPcByImosId = new Map<number, RotationPortCall>();
    const existingBcByImosId = new Map<number, RotationBerthCall>();
    const incomingBcByImosId = new Map<number, RotationBerthCall>();
    const existingCargoByImosId = new Map<number, RotationCargo>();

    for (const pc of comparisonData.existingRotation) {
        if (pc.imosPortCallId != null) existingPcByImosId.set(pc.imosPortCallId, pc);
        for (const bc of pc.berthCalls) {
            if (bc.imosBerthCallId != null) existingBcByImosId.set(bc.imosBerthCallId, bc);
        }
    }
    for (const pc of comparisonData.incomingRotation) {
        for (const bc of pc.berthCalls) {
            if (bc.imosBerthCallId != null) incomingBcByImosId.set(bc.imosBerthCallId, bc);
        }
    }
    for (const c of comparisonData.existingCargoes) {
        if (c.imosCargoId != null) existingCargoByImosId.set(c.imosCargoId, c);
    }

    incomingItems.forEach((incomingItems) => {
        const incomingElement = incomingItems.rotationElement;
        switch (changeDataType) {
            case StowageChangeDataType.RotationBerthCall: {
                const berthCall = incomingElement as RotationBerthCall;
                const existingPortCall = existingPcByImosId.get(berthCall.portCall?.imosPortCallId);
                if (existingPortCall) berthCall.portCallGuid = existingPortCall.portCallGuid;
                break;
            }
            case StowageChangeDataType.RotationCargoOperation: {
                const cargoOp = incomingElement as RotationCargoOperation;
                const existingPortCall = existingPcByImosId.get(cargoOp.berthCall?.portCall?.imosPortCallId);
                const existingBerthCall = existingBcByImosId.get(cargoOp.berthCall?.imosBerthCallId);

                if (existingPortCall) cargoOp.portCallGuid = existingPortCall.portCallGuid;
                cargoOp.berthCallGuid =
                    existingBerthCall?.berthCallGuid ??
                    incomingBcByImosId.get(cargoOp.berthCall?.imosBerthCallId)?.berthCallGuid;

                if (cargoOp.cargo) {
                    const existingCargo = existingCargoByImosId.get(cargoOp.cargo?.imosCargoId);
                    if (existingCargo && existingCargo.cargoGuid !== cargoOp.cargoGuid) {
                        cargoOp.cargo = existingCargo;
                        cargoOp.cargoGuid = existingCargo.cargoGuid;
                    }
                }
                break;
            }
            case StowageChangeDataType.RotationCargo: {
                const cargo = incomingElement as RotationCargo;
                const existingCargo = existingCargoByImosId.get(cargo.imosCargoId);
                if (existingCargo) cargo.cargoGuid = existingCargo.cargoGuid;
                break;
            }
        }
    });
};

const setSyncStatus = (
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    exisitingItems: RotationItem[],
    incomingItems: RotationItem[],
    incomingItemByImosId?: Map<number | UUID, RotationItem>
) => {
    let openPortCall = comparisonData.existingRotation
        .sort((a, b) => +a.voyageNumber - +b.voyageNumber)
        .find((pc) => !isVoyageClosedOrCompleted(pc.voyageStatus) || pc.portCallStatus !== PortCallStatusEnum.Departed);

    if (!openPortCall) {
        openPortCall = comparisonData.incomingRotation
            .sort((a, b) => +a.voyageNumber - +b.voyageNumber)
            .find((pc) => !isVoyageClosedOrCompleted(pc.voyageStatus) || pc.portCallStatus !== PortCallStatusEnum.Departed);
    }

    if (!openPortCall) return;

    const isVoyageOpen = (portCall: RotationPortCall) =>
        !isVoyageClosedOrCompleted(portCall.voyageStatus) ||
        +portCall.voyageNumber >= +openPortCall.voyageNumber ||
        (portCall.turnaroundVoyageNumber && +portCall.turnaroundVoyageNumber >= +openPortCall.voyageNumber);

    const findAssociatedPortCall = (item: RotationItem): RotationPortCall | undefined => {
        switch (changeDataType) {
            case StowageChangeDataType.RotationPortCall:
                return item.rotationElement as RotationPortCall;
            case StowageChangeDataType.RotationBerthCall: {
                const berthCall = item.rotationElement as RotationBerthCall;
                return (
                    comparisonData.existingRotation.find((pc) => pc.berthCalls.some((bc) => bc.berthCallGuid === berthCall.berthCallGuid)) ||
                    berthCall.portCall
                );
            }
            case StowageChangeDataType.RotationCargoOperation: {
                const cargoOperation = item.rotationElement as RotationCargoOperation;
                return (
                    comparisonData.existingRotation.find((pc) => pc.portCallGuid === cargoOperation.portCallGuid) ||
                    cargoOperation.berthCall?.portCall
                );
            }
            case StowageChangeDataType.RotationCargo: {
                const cargo = item.rotationElement as RotationCargo;
                const incomingPortCall = comparisonData.incomingRotation
                    .flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations))
                    .find((co) => co.cargoGuid === cargo.cargoGuid)?.berthCall?.portCall;
                return incomingPortCall;
            }
        }
    };

    const markItemIfInOpenVoyage = (item: RotationItem) => {
        const portCall = findAssociatedPortCall(item);
        item.extraData = { ...item.extraData, voyageNumber: portCall?.voyageNumber };
        if (
            (portCall && isVoyageOpen(portCall)) ||
            (!portCall && (item.rotationElement as any).voyageNumber && (item.rotationElement as any).voyageNumber >= openPortCall.voyageNumber)
        ) {
            item.extraData = { ...item.extraData, isChangeInOpenVoyage: true };
        } else if (
            changeDataType === StowageChangeDataType.RotationCargo &&
            (item.rotationElement as RotationCargo).voyageNumber >= openPortCall.voyageNumber
        ) {
            item.extraData = { ...item.extraData, isChangeInOpenVoyage: true, isRogueCargo: true };
        }
    };

    // Mark existing items
    exisitingItems.forEach(markItemIfInOpenVoyage);

    // Mark incoming items
    incomingItems.forEach(markItemIfInOpenVoyage);

    // For existing items that have corresponding incoming items, ensure they get the correct status
    // based on the incoming item's voyage status
    exisitingItems.forEach((existingItem) => {
        const existingId = getItemImosId(changeDataType, existingItem.rotationElement);
        const correspondingIncomingItem = incomingItemByImosId?.get(existingId) ??
            incomingItems.find(
                (incomingItem) =>
                    existingId === getItemImosId(changeDataType, incomingItem.rotationElement)
            );

        if (correspondingIncomingItem && correspondingIncomingItem.extraData?.isChangeInOpenVoyage) {
            existingItem.extraData = {
                ...existingItem.extraData,
                isChangeInOpenVoyage: true,
                voyageNumber: correspondingIncomingItem.extraData.voyageNumber,
            };
        }
    });
};

const getMergedItems = (
    incomingItems: RotationItem[],
    findItemByMergeProperties: (
        rotationElement: RotationElement,
        incomingList: RotationPortCall[] | RotationCargo[],
        extraArgs?: any
    ) => RotationElement,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    transitOperations: RotationPortCall[],
    createChangeData: (item: RotationElement) => Partial<RotationElement>
): StowagePlanChange[] => {
    return incomingItems
        .filter((incomingItem) =>
            findItemByMergeProperties(
                incomingItem.rotationElement,
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
                transitOperations
            )
        )
        .map((incomingItem) => {
            const changeData = createChangeData(incomingItem.rotationElement) as any;

            changeData.extraData = {
                ...incomingItem.extraData,
                descriptions: [`${getDescriptor(changeDataType, incomingItem.rotationElement, 'Merged')}`],
            };

            const existingItem = findItemByMergeProperties(
                incomingItem.rotationElement,
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
                transitOperations
            );

            const changeDataGuid = getChangeDataGuid(changeDataType, existingItem);
            setChangeDataGuidByType(changeDataType, incomingItem.rotationElement, changeDataGuid, comparisonData);
            setIgnoredMergePropertiesInChangeData(changeData, changeDataType, existingItem);

            return createStowagePlanChange(comparisonData.stowagePlanGuid, changeDataGuid, changeDataType, changeData, true);
        });
};

const setChangeDataGuidByType = (
    changeDataType: StowageChangeDataType,
    incomingElement: RotationElement,
    changeDataGuid: UUID,
    comparisonData: ComparisonData
) => {
    setChangeDataGuid(changeDataType, incomingElement, changeDataGuid);
    switch (changeDataType) {
        case StowageChangeDataType.RotationPortCall: {
            const portCall = incomingElement as RotationPortCall;
            portCall.berthCalls.forEach((berthCall) => {
                berthCall.portCallGuid = changeDataGuid;
                berthCall.cargoOperations.forEach((cargoOperation) => {
                    cargoOperation.portCallGuid = changeDataGuid;
                });
            });
            break;
        }
        case StowageChangeDataType.RotationBerthCall: {
            const berthCall = incomingElement as RotationBerthCall;
            berthCall.cargoOperations.forEach((cargoOperation) => {
                cargoOperation.berthCallGuid = changeDataGuid;
            });
            break;
        }
        case StowageChangeDataType.RotationCargo: {
            const cargo = incomingElement as RotationCargo;
            const cargoOperationForCargo = comparisonData.incomingRotation
                .flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations))
                .find((co) => co.imosCargoId === cargo.imosCargoId);

            if (cargoOperationForCargo) {
                cargoOperationForCargo.cargoGuid = cargo.cargoGuid;
            }

            break;
        }
    }
};

const getDeletedItems = (
    existingItems: RotationItem[],
    incomingByImosId: Map<number | UUID, RotationElement>,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    mergedItems: StowagePlanChange[]
): StowagePlanChange[] => {
    return existingItems
        .filter(
            (existingItem) =>
                !isManualCall(existingItem.rotationElement) &&
                !incomingByImosId.has(getItemImosId(changeDataType, existingItem.rotationElement)) &&
                !mergedItems.find((mergedItem) => mergedItem.changeDataGuid === getChangeDataGuid(changeDataType, existingItem.rotationElement))
        )
        .map((existingItem) => {
            const change = createDeleteStowagePlanChange(
                comparisonData.stowagePlanGuid,
                getChangeDataGuid(changeDataType, existingItem.rotationElement),
                changeDataType
            );
            change.data = JSON.stringify({
                extraData: { ...existingItem.extraData, descriptions: [`${getDescriptor(changeDataType, existingItem.rotationElement, 'Deleted')}`] },
            });
            return change;
        });
};

const getAddedItems = (
    incomingItems: RotationItem[],
    existingByImosId: Map<number | UUID, RotationElement>,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    transitOperations: RotationPortCall[],
    createChangeData: (item: RotationElement) => Partial<RotationElement>,
    mergedItems: StowagePlanChange[]
): StowagePlanChange[] => {
    return incomingItems
        .filter((incomingItem) => {
            const incomingImosItemId = getItemImosId(changeDataType, incomingItem.rotationElement);
            let isAdded = !existingByImosId.has(incomingImosItemId);

            if (isAdded && mergedItems.length > 0) {
                isAdded = !mergedItems.find((mergedItem) => {
                    const data = JSON.parse(mergedItem.data);
                    const mergedImosItemId = getItemImosId(changeDataType, data);
                    return incomingImosItemId === mergedImosItemId;
                });
            }

            if (isAdded && changeDataType === StowageChangeDataType.RotationCargoOperation) {
                isAdded = !hasMatchingSpiltCargoOperations(
                    incomingItem.rotationElement as RotationCargoOperation,
                    comparisonData.existingRotation,
                    transitOperations
                );
            }

            return isAdded;
        })
        .flatMap((incomingItem) => {
            const stowagePlanChanges: StowagePlanChange[] = [];
            const changeData = createChangeData(incomingItem.rotationElement) as any;
            const incomingElement = incomingItem.rotationElement;

            changeData.extraData = { ...incomingItem.extraData, descriptions: [`${getDescriptor(changeDataType, incomingElement, 'Added')}`] };
            stowagePlanChanges.push(
                createStowagePlanChange(
                    comparisonData.stowagePlanGuid,
                    getChangeDataGuid(changeDataType, incomingItem.rotationElement),
                    changeDataType,
                    changeData,
                    true
                )
            );
            return stowagePlanChanges;
        });
};

const getUpdatedItems = (
    existingItems: RotationItem[],
    incomingByImosId: Map<number | UUID, RotationElement>,
    incomingItemByImosId: Map<number | UUID, RotationItem>,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    compareItems: (stowagePlanGuid: UUID, existingItem: RotationItem, incomingItem: RotationItem) => StowagePlanChange | null
): StowagePlanChange[] => {
    const updatedItems = existingItems
        .map((existingItem) => {
            const existingId = getItemImosId(changeDataType, existingItem.rotationElement);
            const incomingElement = incomingByImosId.get(existingId);
            if (!incomingElement) return null;

            const incomingItemWithExtraData = incomingItemByImosId.get(existingId);

            const incomingItem = {
                rotationElement: incomingElement,
                extraData: incomingItemWithExtraData?.extraData,
            };

            return compareItems(comparisonData.stowagePlanGuid, existingItem, incomingItem);
        })
        .filter(Boolean) as StowagePlanChange[];

    return updatedItems;
};

/**
 * Finds manual cargo operations that would be orphaned by a berth call deletion during IMOS sync,
 * and generates update changes to re-parent them to a replacement berth call under the same port call.
 *
 * The problem: When IMOS replaces a berth call (delete old + add new), manual cargo operations under
 * the deleted berth are protected from deletion (!isManualCall check), but their berthCallGuid still
 * references the deleted berth. During plan rebuild, stowageDataItemsBuilder.updateCargoOperation()
 * silently drops them because the berth call no longer exists. This also applies to manual berth calls
 * under deleted port calls.
 */
const getReParentedManualCargoOperations = (comparisonData: ComparisonData, changes: StowagePlanChange[]): StowagePlanChange[] => {
    const reParentChanges: StowagePlanChange[] = [];

    // Collect deleted berth call GUIDs
    const deletedBerthCalls = changes
        .filter((c) => c.changeDataType === StowageChangeDataType.RotationBerthCall && c.isDeleted)
        .map((c) => c.changeDataGuid);

    if (deletedBerthCalls.length === 0) return reParentChanges;

    // Build a mapping of deleted port call GUID → replacement port call GUID.
    // When IMOS replaces a port call, both the port call and its berth calls get new GUIDs.
    // We match old→new by finding added port calls at the same sequence position or by
    // checking if the incoming rotation has a port call that replaced the deleted one.
    const deletedPortCallGuids = new Set(
        changes.filter((c) => c.changeDataType === StowageChangeDataType.RotationPortCall && c.isDeleted).map((c) => c.changeDataGuid)
    );
    const existingPortCallGuids = new Set(comparisonData.existingRotation.map((pc) => pc.portCallGuid));
    const addedPortCallChanges = changes.filter(
        (c) => c.changeDataType === StowageChangeDataType.RotationPortCall && !c.isDeleted && !existingPortCallGuids.has(c.changeDataGuid)
    );
    const portCallReplacementMap = new Map<string, string>();
    // Match deleted port calls to replacement (added) ones by portId and portOrderNumber
    for (const existingPC of comparisonData.existingRotation) {
        if (!deletedPortCallGuids.has(existingPC.portCallGuid)) continue;
        for (const addedChange of addedPortCallChanges) {
            if (portCallReplacementMap.has(existingPC.portCallGuid)) break;
            const incomingPC = comparisonData.incomingRotation.find((pc) => pc.portCallGuid === addedChange.changeDataGuid);
            if (!incomingPC) continue;
            // Prefer exact match on both portId and portOrderNumber
            if (incomingPC.portId === existingPC.portId && incomingPC.portOrderNumber === existingPC.portOrderNumber) {
                portCallReplacementMap.set(existingPC.portCallGuid, addedChange.changeDataGuid);
            }
        }
        // Fallback: match by portId alone if no portOrderNumber match found
        if (!portCallReplacementMap.has(existingPC.portCallGuid)) {
            for (const addedChange of addedPortCallChanges) {
                const incomingPC = comparisonData.incomingRotation.find((pc) => pc.portCallGuid === addedChange.changeDataGuid);
                if (incomingPC && incomingPC.portId === existingPC.portId) {
                    portCallReplacementMap.set(existingPC.portCallGuid, addedChange.changeDataGuid);
                    break;
                }
            }
        }
    }

    // Collect added berth calls with their data (to find replacements)
    const addedBerthCallChanges = changes.filter((c) => c.changeDataType === StowageChangeDataType.RotationBerthCall && !c.isDeleted);

    // Find manual cargo operations under deleted berth calls
    for (const portCall of comparisonData.existingRotation) {
        for (const berthCall of portCall.berthCalls ?? []) {
            if (!deletedBerthCalls.includes(berthCall.berthCallGuid)) continue;

            const manualCargoOps = (berthCall.cargoOperations ?? []).filter((co) => isManualCall(co));
            if (manualCargoOps.length === 0) continue;

            // Determine the effective port call GUID to search under.
            // If the port call itself was also replaced, search under the replacement GUID.
            const effectivePortCallGuid = portCallReplacementMap.get(portCall.portCallGuid) ?? portCall.portCallGuid;

            // Find a replacement berth call under the effective port call
            const replacementBerthCallGuid = findReplacementBerthCallGuid(effectivePortCallGuid, addedBerthCallChanges, comparisonData);

            if (!replacementBerthCallGuid) continue;

            // Derive sync status (isChangeInOpenVoyage) from the deleted berth call's change,
            // so the re-parent change passes the open/closed voyage filter in the ChangeLog.
            const deletedBerthCallChange = changes.find(
                (c) => c.changeDataType === StowageChangeDataType.RotationBerthCall && c.isDeleted && c.changeDataGuid === berthCall.berthCallGuid
            );
            const deletedBerthCallExtraData = deletedBerthCallChange?.data ? JSON.parse(deletedBerthCallChange.data)?.extraData : {};

            // Generate update changes to re-parent each manual cargo operation
            for (const cargoOp of manualCargoOps) {
                // Skip if an update change already exists for this cargo operation
                const alreadyHasChange = changes.some(
                    (c) => c.changeDataType === StowageChangeDataType.RotationCargoOperation && c.changeDataGuid === cargoOp.cargoOperationGuid
                );
                if (alreadyHasChange) continue;

                // Include both portCallGuid and berthCallGuid so the cargo op is placed
                // correctly even when the port call was also replaced during sync.
                const changeData: Record<string, unknown> = {
                    berthCallGuid: replacementBerthCallGuid,
                    extraData: {
                        isChangeInOpenVoyage: deletedBerthCallExtraData?.isChangeInOpenVoyage ?? true,
                        voyageNumber: deletedBerthCallExtraData?.voyageNumber ?? portCall.voyageNumber,
                        hideFromChangeLog: true,
                    },
                };

                // If the port call was also replaced, update portCallGuid too
                if (portCallReplacementMap.has(portCall.portCallGuid)) {
                    changeData.portCallGuid = effectivePortCallGuid;
                }

                reParentChanges.push(
                    createStowagePlanChange(
                        comparisonData.stowagePlanGuid,
                        cargoOp.cargoOperationGuid as UUID,
                        StowageChangeDataType.RotationCargoOperation,
                        changeData,
                        true
                    )
                );
            }
        }
    }

    return reParentChanges;
};

/**
 * Finds the GUID of a replacement berth call (an added berth call under the same port call).
 * Prefers matching by the incoming IMOS rotation data to find the correct berth.
 */
const findReplacementBerthCallGuid = (
    portCallGuid: UUID | string,
    addedBerthCallChanges: StowagePlanChange[],
    comparisonData: ComparisonData
): UUID | null => {
    // Look through added berth call changes to find one under the same port call
    for (const change of addedBerthCallChanges) {
        const data = change.data ? JSON.parse(change.data) : null;
        if (!data) continue;

        if (data.portCallGuid === portCallGuid) {
            return change.changeDataGuid as UUID;
        }
    }

    // Also check incoming rotation for new berth calls that map to the same port call
    // (the portCallGuid on incoming berth calls may have been aligned by assignCorrectGuids)
    for (const incomingPortCall of comparisonData.incomingRotation) {
        if (incomingPortCall.portCallGuid !== portCallGuid) continue;

        for (const berthCall of incomingPortCall.berthCalls ?? []) {
            // Check if this incoming berth call is one of the added berth calls
            const isAdded = addedBerthCallChanges.some((c) => c.changeDataGuid === berthCall.berthCallGuid);
            if (isAdded) {
                return berthCall.berthCallGuid as UUID;
            }
        }
    }

    return null;
};
